import {_, classNames, React, useLocalStore, useLayoutEffect, observer, pluralize, useAsObservableSource, toHeaderCase} from '@api'
import {List} from '@material-ui/core'
import {CatalogLibrariesContextMenu} from '@modules/catalog'
import {AssignableLibraryField} from '@modules/catalog/sidebar/CatalogLibrariesPanel/AssignableLibraryField'
import {LoadingIndicator} from '@modules/common'
import {IContextMenuItems} from '@modules/contextMenus'
import {AbstractSidebarStore, SidebarAlign} from '@modules/drawer'
import {ui, prettyPrint, ICatalogFieldConfig, WorkspaceConfig, ISectionConfig, LibraryItemConfigWrapper, LibraryItemConfigWrapperStore, useCatalog, onSiteError, WorkspacesStore, StoresInstance, isTagField, site} from '@stores'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {removeUndefinedsNested} from '@utils'
import {CollapsibleStateful} from 'centric-ui-components'
import {action, computed, observable, runInAction, when} from 'mobx'
import {Fragment, useContext} from 'react'
import * as css from './CatalogLibraries.css'
import {CatalogLibrariesTreeView} from './CatalogLibrariesTreeView'

interface MyProps {
  className?: string
  title?: string
  align: SidebarAlign
}

export const LIBRARY_SECTION = '__LIBRARIES__'
export type CatalogLibrariesPanelSectionSettings = {
  open?: boolean
}

export type CatalogLibrariesPanelSettings = {
  sections?: {[section: string]: CatalogLibrariesPanelSectionSettings}
}

export const CatalogLibraries = observer<MyProps>(function CatalogLibraries({className, title, align}) {
  const store = useVipLocalStore((ws) => new CatalogLibrariesStore(ws, align)),
    {
      strings, flexLayout: {flexEnabled}, libraryCount,
      catalog: {workspaceConfig, hasLoadedConfig, itemCount, loadingCounts, settings: {catalogLibrariesPanel: {sections: sectionSettings}}, es: {catalogSeasonalTerms, refetchId}},
      stores: {
        storeId},
      libraries, onContextMenu, loading, sidebar: {tab, open, selectedPathItem}
    } = store

  useLayoutEffect(() => {
    runInAction(() => {
      store.loading = true
      store.libraries = null
    })
  }, [storeId])

  // Old UR demand - causes havoc in flex layout
  // useLayoutEffect(() => {
  //   if (sidebar.left.tab === 0  && workspaces.visibleLibraries.length == 1 && _.isEmpty(assignableFields)) {
  //     const libraryId = workspaces.visibleLibraries[0].id
  //     if (sidebar.left.selectedPathItem?.libraryId != libraryId) {
  //       navigateToLibrary({libraryId})
  //     }
  //   }
  // }, [sidebar.left.tab, sidebar.left.path, workspaces.visibleLibraries])

  useLayoutEffect(() => {
      if (flexEnabled || (tab == 0 && !selectedPathItem?.libraryId && open && !store.everVisible)) {
        store.everVisible = true
      }
    }, [flexEnabled, tab, selectedPathItem?.libraryId, open])

  useLayoutEffect(() => {
      !loadingCounts && store.loadCounts()
    }, [loadingCounts, workspaceConfig, hasLoadedConfig, catalogSeasonalTerms, itemCount, refetchId, storeId ])

  return (
    <CatalogLibrariesStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           onContextMenu={onContextMenu}
           data-type='CatalogLibraries'>
        <List disablePadding className={css.details}>
          {
            loading && !libraries ?
            <LoadingIndicator className={css.loader}/> :
            <Fragment>
              {!_.isEmpty(libraries) &&
                 <CollapsibleStateful
                   className={classNames(css.librariesCollapsible, css.collapsible)}
                   data-count={libraryCount}
                   classes={{wrapperInner: css.statefulWrapperInner}}
                   title={<TitleAndCount title={title ?? strings.libraries} count={null}/>} //libraryItems.length
                   open={sectionSettings[LIBRARY_SECTION]?.open ?? true}
                   onToggleOpen={(open) => {
                     sectionSettings[LIBRARY_SECTION] = {...(sectionSettings[LIBRARY_SECTION] ?? {}), open}
                   }}
                 >
                   <CatalogLibrariesTreeView/>
                 </CollapsibleStateful>}
              <CatalogLibrariesFieldsBySection/>
            </Fragment>}
        </List>
      </div>
    </CatalogLibrariesStoreContext.Provider>
  )
});

CatalogLibraries.defaultProps = {}

const CatalogLibrariesFieldsBySection = observer(function CatalogLibrariesFieldsBySection() {
    const {strings, orderedSectionIds} = useCatalogLibrariesStore(),
      catalog = useCatalog()

  // logJS({visibleFieldsBySection, sectionIdsInOrder})
  return (<Fragment>
    {orderedSectionIds
      .filter(id => id.split('.').length <= 2)  // Only render 1-deep relationships as top level groups in library sidebar
      .map((sectionId, j) => {
      const section = catalog.sectionById[sectionId]

      // const _fieldByName = section?.workspace ? workspaces.wrapperById[section.workspace._id]?.catalog?._fieldByName : catalog._fieldByName
      // logJS({msg: `Load section ${sectionId} - ${section?.label}`, section, sectionId, fields})
      // if (!section) {
      //   console.warn(`No section found for ${sectionId}`, section)
      //   return null
      // }
      return (
        <Fragment key={j}>
          <SectionLibraryFields section={section} />
      </Fragment>)
    })}

    </Fragment>)
});

const SectionLibraryFields = observer<{section: ISectionConfig}>(function SectionLibraryFields({section}) {
  const {onContextMenu, visibleFieldsBySection} = useCatalogLibrariesStore(),
    catalog = useCatalog()

  if (_.isUndefined(section)) {
    return null
  }

  const {workspaces, settings: {catalogLibrariesPanel: {sections: sectionSettings}}} = catalog,
    {id: sectionId} = section,
    fields: Array<ICatalogFieldConfig> = visibleFieldsBySection[sectionId],
    _fieldByName = section?.workspace ? workspaces.wrapperById[section.workspace._id]?.catalog?._fieldByName : catalog._fieldByName,
    title = toHeaderCase(pluralize(section?.label ?? _fieldByName[section?.field]?.label ?? _fieldByName[_.first(fields)?.field]?.label ?? catalog.catalogItemName, 1))

    return (
      <CollapsibleStateful className={classNames(css.fieldsByWorkspacePanel, css.collapsible)}
                           data-section={sectionId}
                           style={{'--depth': (sectionId || '').split('.').length - 1} as any}
                           open={sectionSettings[sectionId]?.open ?? sectionId == catalog.workspaceConfig._id}
                           classes={{wrapperInner: css.statefulWrapperInner}}
                           onToggleOpen={(open) => {
                             sectionSettings[sectionId] = {...(sectionSettings[sectionId] ?? {}), open}
                           }}
                           onContextMenu={e => onContextMenu(e, {section})}
                           title={<TitleAndCount title={title} count={null}/>} // !groupBy.counts || sectionId === catalog.workspace._id ? null : groupBy.counts[section.field]?.value
      >
        {fields.map((f, i) => <AssignableLibraryField key={f.field ?? i} field={f}/>)}
        {section.field && <ForeignKeyWorkspaceNested parentField={section.field}/>}
      </CollapsibleStateful>
    )
});

const ForeignKeyWorkspaceNested = observer<{ parentField: string}>(function ForeignKeyWorkspaceNested({parentField}) {
    const {orderedSectionIds} = useCatalogLibrariesStore(),
      depth = parentField.split('.').length,
      catalog = useCatalog(),
      childrenSections = orderedSectionIds.filter(id => id.startsWith(`${parentField}.`) && id.split('.').length === depth + 2)  // + 2 because we will always have a +1 as we do field.esIndex for id of section

    // console.log({parentField, depth, childrenSections})
    return (<Fragment>
      {childrenSections.map((section, i) => <Fragment key={i}>
        <SectionLibraryFields section={catalog.sectionById[section]} key={i} />
      </Fragment>)}
    </Fragment>)
});

const TitleAndCount = observer<{title: React.ReactNode, count?: React.ReactNode}>(function TitleAndCount({title, count}) {
    const {} = useCatalogLibrariesStore()

  return (
    <div className={css.titleAndCount}>
      <span className={css.title}>{title}</span>
      {_.isNumber(count) && <span className={css.count}>({count})</span>}
    </div>
  )
})

export interface ICatalogLibrariesContextMenuExtra {
  field?: ICatalogFieldConfig
  workspace?: WorkspaceConfig
  section?: ISectionConfig
}

export class CatalogLibrariesStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance,public align: SidebarAlign) {
    super(stores)

    if (stores.flexLayout.knownTabs?.librariesTab?.extraData) {
      stores.flexLayout.knownTabs.librariesTab.extraData.store = this
    }

    stores.sidebar[align].catalogLibraries = this

    stores.catalog.toDispose.push(
      when(() => stores.deployments.hasLoadedDynamicConfig, () => this.c8.fetchAllEnumValuesC8()),
      when(() => stores.deployments.hasLoadedDynamicConfig, () => this.c8.getAllLookupItems())
    )
  }

  get catalog() {
    return this.flexLayout.activeOrLastCatalogRoot?.catalog ?? this.stores.catalog
  }

  @computed get orderedLibraries() : Array<LibraryItemConfigWrapper> {
    return _.orderBy(this.visibleLibraries, l => !l.workspaceId || l.workspaceId === this.catalog.workspaceConfig?._id ? ` ${l.labelWithWorkspace}` : l.labelWithWorkspace)
  }

  @computed get visibleLibraries() {
    const {stores, workspaces: {workspaceConfig}} = this
    return (this.libraries ?? []).filter(l => !l.divider && l.visible !== false && (!workspaceConfig?.filterLibraries || workspaceConfig?.filterLibraries(l, stores)))
  }

  @computed get libraryByWorkspace() {
    return _.groupBy(this.orderedLibraries, l => l.workspace?._id ?? l.workspaceId ?? this.catalog.id)
  }

  // @computed get filteredLibraryByWorkspace() {
  //   const {libraryByWorkspace} = this
  //   return libraryByWorkspace[workspaceId]
  // }

  @computed get data() {
    const {libraries} = this

    if (!libraries) return null

    return {
      libraries,
      attributes: []
    }
  }

  /**
   * Removes dividers - real library count
   */
  @computed get libraryCount() {
    return this.orderedLibraries.filter(l => l && !l.divider).length
  }

  @observable.ref libraries: Array<LibraryItemConfigWrapper> = null

  @computed get searchPayload() {
    const {workspaces, es, catalog, catalog: {workspaceConfig, hasLoadedConfig, assignableFields, es: {catalogSeasonalTerms}}} = this
      //bySection = _.groupBy(assignableFields, f => f.section)

    if (!workspaceConfig || !hasLoadedConfig) return null

    //logJS(bySection)
    const {visibleLibraries} = workspaces
    return {
      size: 0,
      query: {
        bool: {
          minimum_should_match: 1,
          should: _.uniqBy([
            {term: {_index: es.indexes.customAttribute}},
            {term: {_index: es.indexes.tag}},
            {term: {_index: workspaceConfig.catalog?.index}},
            ...(_.uniqBy(visibleLibraries, l => l.id).map(l => l.config.index ? ({term: {_index: l.config.index}}) : null).filter(_.identity) || [])
          ], l => l.term._index),
          // ...ui.settings?.deleteInfo?.markAsDelete && {filter: [es.filterForNonDeletedItems]}
        }
      },
      aggs: {
        // Library Counts
        ...(visibleLibraries || []).reduce((acc, fieldDef) => {
          //console.log({libraries, acc, fieldDef})

          const {id, config: {name, seasonal, attrName, agg, where}} = fieldDef
          let {config: {index}} = fieldDef
          if (where && !index) {
            index = catalog.index
          }

          // console.log({index, id, name, attrName, fieldDef})

          if (index) {
            const must = seasonal ? catalogSeasonalTerms : [],
            fetchTagsByLibrary = name === "tags" ? catalog.getTagLibraryClause() : undefined;
            if(fetchTagsByLibrary) {
              must.push(fetchTagsByLibrary)
            }
            if (where) {
              must.push(...where)
            }

            return _.assign(acc, {
              [id]: {
                filter: {
                  bool: {
                    must: [
                      ...must,
                      {term: {_index: index}}
                    ]
                  }
                },
                // aggs: {
                //   count: {value_count: {field: '_id'}}
                // }
              }
            })
          } else if (attrName) {
            return _.assign(acc, {
              [id]: {
                filter: {
                  bool: {
                    must: [
                      {term: {_index: es.indexes.customAttribute}},
                      {match: {name: attrName}}
                    ]
                  }
                },
                aggs: {
                  count: {value_count: {field: 'tagType.tags.id.keyword'}}
                }
              }
            })
          } else if (agg) {
            return _.assign(acc, agg)
          } else return acc
        }, {}),

        'assignableFields': this.fieldsByWorkspaceIdAggs,

        // 'tagTypes': {
        //   filter: {bool: {}},
        //   aggs: {
        //     ...caDefs.filter(ca => ca.tagType).reduce((acc, {tagType: tt}) => ({
        //       ...acc,
        //       [tt.id]: {
        //         filter: {
        //           bool: {
        //             must: [
        //               {term: {type: tt.id}},
        //               {term: {_index: es.indexes.tag}}
        //             ]
        //           }
        //         }
        //       }
        //     }), {})
        //   }
        // }
      }
    }
  }

  @computed get fieldsByWorkspaceIdAggs() {
    const {catalog, es} = this

    return {
      filter: /*catalog.es.catalogItemFilter.filter?.query ??*/ {bool: {}},  // Turn of seasonal restrictions of the # of values to avoid mismatches later YFTS-5026
      aggs: {
        ...catalog.assignableFields.filter(f => isTagField(f)).reduce(
          (acc, f) => {
            return _.assign(acc, {
              [f.def.tagType]: {
                filter: {bool: {filter: [{term: {_index: es.indexes.tag}}, {term: {type: f.def.tagType}}]}},
                aggs: {
                  tags: {
                    top_hits: {
                      size: 1000,
                      _source: ['name', 'description', 'id']
                    }
                  }
                }
              }
            })
          }, {}),
        [catalog.workspace.id]: {
          filter: {
            term: {
              _index: catalog.index
            }
          },
          aggs: catalog.assignableFields
            .filter(f => !isTagField((f)))  // Tag counts are wrong - would need to query vip-tag for total # of tags and merge it into response TODO
            .reduce((acc, field : ICatalogFieldConfig) => {
            /*if (field?.def?.tagType) {
              return ({
                ...acc,
                [field.field]: {
                  filter: {
                    bool: {
                      must: [
                        {term: {type: field.def.tagType}},
                        {term: {_index: es.indexes.tag}}
                      ]
                    }
                  }
                }
              })
            } else {*/
              const sectionConfig = catalog.sectionConfigFor(field.section)
              return _.assign(
                acc, {
                [field.field]: (
                  sectionConfig?.viewOptions?.field
                  ? {
                      nested: {path: sectionConfig.path},
                      aggs: {
                        count: {
                          cardinality: {field: catalog.esFieldFor(field)}
                        }
                      }
                    }
                  : {
                      cardinality: {field: catalog.esFieldFor(field, true)}
                    })
              })
            // }
          }, {})
        }
      }
    }
  }

  @observable.ref private _countAggs: { [id: string]: { value?: number, doc_count?: number, count?: { value?: number } } } = {}
  @computed get countById(): { [id: string]: { value?: number, doc_count?: number, count?: { value?: number} } } {
    return {...this.assignableEnumsC8, ...this._countAggs}
  }

  @action onContextMenu = (e, extra ?: ICatalogLibrariesContextMenuExtra) => {
    this.contextMenu.onComponentContextMenu(e, this, extra)
  }

  @computed get contextMenuItems() {
    return <CatalogLibrariesStoreContext.Provider value={this}>
      <CatalogLibrariesContextMenu />
    </CatalogLibrariesStoreContext.Provider>
  }

  @computed get orderedSectionIds() {
    const {catalog} = this
    return _.orderBy(_.keys(this.visibleFieldsBySection), id => id === catalog.workspace.id ? ' ' : (catalog.sectionById[id]?.label ?? id))
  }

  @computed get visibleFieldsBySection() {
    const {catalog: {workspaceConfig, assignableFields, hideAttributeFromSidebar}} = this

    const fieldsToRender = _.uniqBy(_.concat(assignableFields).filter(_.identity), f => f.field)

    // return _.groupBy(fieldsToRender, f => (f?.section && !hideAttributeFromSidebar.includes(f?.section)) ?? (workspaceConfig._id && !hideAttributeFromSidebar.includes(workspaceConfig._id))) /* catalog.sectionIdForField(f) */
    const groupByData = _.groupBy(fieldsToRender, f => {
      if(f?.section) {
        if(hideAttributeFromSidebar.has(f?.section)) {
          return null
        }
        return f?.section
      } else {
        if(hideAttributeFromSidebar.has(workspaceConfig._id)) {
          return null
        }
        return workspaceConfig._id
      }
    })

    return groupByData ?? []
  }

  @observable loading = true
  @observable everVisible = false

  @computed  get assignableEnumsC8() {
    const {catalog: {assignableFields}, c8} = this
    return assignableFields?.filter(f => (f.def?.type === "Enum" || f.def?.type === "EnumList"))?.reduce((acc,{field,def})=> _.assign(acc, { [field]: {value: def?.enumValues?.length ?? c8?.enumByName.get(def?.enumType)?.length ?? null }}), {}) ?? {}
  }

  @action loadCounts = async () => {
    const {workspaces, es, catalog, catalog: {workspaceConfig, hasLoadedConfig}} = this
    if (!workspaceConfig || !hasLoadedConfig) return null

    const {visibleLibraries} = workspaces
    if (!visibleLibraries) {
      this.loading = false
      return null
    }

    const {searchPayload} = this
    this.loading = true

    try {
      const {aggregations} = await es.search(
        removeUndefinedsNested({
          meta: 'CatalogLibraries.loadCounts',
          index: null,
          body: searchPayload
        }), {log: {query: false, result: false}})
      //store.libraries =
      const r = visibleLibraries?.map(({config: v}) => new LibraryItemConfigWrapperStore(catalog, v)) ?? []
      //console.log(aggregations, r, visibleLibraries)

      runInAction(`Save top level catalog library counts`,() => {
        this.libraries = r
        // console.log(aggregations)
        //this.countById = {...(aggregations ? aggregations['assignableFields'][catalog.workspace.id] : {}), ...this.assignableEnumsC8}
        this._countAggs = aggregations ? {...aggregations, ...aggregations['assignableFields'][catalog.workspace.id]} : {}
        // logJS({aggregations, c: this.countById})
      })
      return r
    }
    catch (err) {
      onSiteError(err)
      console.log(prettyPrint(searchPayload))
    }
    finally {
      this.loading = false
    }
  }

  get sidebar(): AbstractSidebarStore {
    return this.stores.sidebar[this.align]
  }

  @action collapseAll = () => {
    const {catalog: {settings: {catalogLibrariesPanel: {sections}}}} = this,
      {visibleFieldsBySection} = this

    _.keys(visibleFieldsBySection).forEach(k => sections[k] = {...(sections[k] ?? []), open: false})
    sections[LIBRARY_SECTION] = {...(sections[LIBRARY_SECTION] ?? {}), open: false}
  }

  @action expandAll = () => {
    const {catalog: {settings: {catalogLibrariesPanel: {sections}}}} = this,
      {visibleFieldsBySection} = this

    _.keys(visibleFieldsBySection).forEach(k => sections[k] = {...(sections[k] ?? []), open: true})
    sections[LIBRARY_SECTION] = {...(sections[LIBRARY_SECTION] ?? {}), open: true}
  }
}

export const CatalogLibrariesStoreContext = React.createContext<CatalogLibrariesStore>(undefined)
export const useCatalogLibrariesStore = () => useContext(CatalogLibrariesStoreContext)




// WEBPACK FOOTER //
// ./src/modules/catalog/sidebar/CatalogLibrariesPanel/CatalogLibraries.tsx